/* eslint-disable max-lines */
/* eslint-disable complexity */
import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { FormInputContainer } from '@strata/react-form-input-container';
import { NumberInputFormik } from '@strata/react-inputs';
import {
    ComponentTypes,
    ControlTextMap,
    formatQuestionLabel,
    LogicOperatorTextMap,
} from '@strata/react-question-tree';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import classNames from 'classnames';
import { FieldArray } from 'formik';
import PropTypes from 'prop-types';
import { Fragment, useMemo } from 'react';

const NegationOptions = [
    { name: 'is', value: undefined },
    { name: 'is not', value: 'NOT' },
];

const OperatorOptions = Object.entries(LogicOperatorTextMap).map(([key, value]) => ({
    name: value,
    value: key,
}));

const getControlOptions = (question) => {
    switch (question.component) {
        case ComponentTypes.NUMBER_INPUT:
            return [{ name: ControlTextMap.INTEGERINPUT, value: 'INTEGERINPUT' }];
        case ComponentTypes.RADIO:
            return [{ name: question.question_text.replaceAll(`<br />`, ' '), value: undefined }];
        case ComponentTypes.MULTI_SELECT:
            return [
                { name: question.question_text.replaceAll(`<br />`, ' '), value: undefined },
                { name: ControlTextMap.COUNT, value: 'COUNT' },
            ];
        default:
            return [];
    }
};

const parenComponent = ({
    isStartParen,
    question,
    rule,
    conditionArrHelpers,
    conditionIndex,
    readOnly,
}) =>
    question.component === ComponentTypes.MULTI_SELECT && rule.conditions.length > 1 ? (
        <div style={{ width: 46, flexShrink: 0 }}>
            <SuggestSingleSelectFormik
                id={`${conditionArrHelpers.name}.${conditionIndex}.${
                    isStartParen ? 'startParen' : 'endParen'
                }`}
                placeholderText=""
                disabled={readOnly}
                values={isStartParen ? ['('] : [')']}
                validate={() => {
                    const parens = [];
                    rule.conditions.forEach((condition) => {
                        if (condition.startParen) {
                            parens.push(condition.startParen);
                        }
                        if (condition.endParen) {
                            parens.push(condition.endParen);
                        }
                    });

                    let startParenCount = 0;
                    parens.every((bracket) => {
                        if (bracket === '(') {
                            startParenCount += 1;
                            return true;
                        }

                        // Handle end paren
                        startParenCount -= 1;
                        return startParenCount >= 0;
                    });
                    return startParenCount !== 0 ? 'Missing Paren' : undefined;
                }}
            />
        </div>
    ) : null;

// condition: { condition, startParen, control, operator, value, endParen }
const getComponentsForCondition = ({
    condition,
    question,
    rule,
    conditionArrHelpers,
    conditionIndex,
    controlOptions,
    readOnly,
}) => {
    const onChange = (updatedCondition) =>
        conditionArrHelpers.replace(conditionIndex, updatedCondition);

    const conditionOptions = [
        ...(question.component !== ComponentTypes.RADIO ? [{ name: 'and', value: 'AND' }] : []),
        { name: 'or', value: 'OR' },
    ];

    let conditionComponent = null;
    if (
        question.component === ComponentTypes.NUMBER_INPUT ||
        (question.component === ComponentTypes.MULTI_SELECT && condition.control === 'COUNT')
    ) {
        conditionComponent = (
            <>
                <div style={{ gridColumn: conditionIndex === 0 ? '2 / span 2' : undefined }}>
                    <SuggestSingleSelectFormik
                        id={`${conditionArrHelpers.name}.${conditionIndex}.operator`}
                        required
                        disabled={readOnly}
                        values={OperatorOptions}
                        selectedItem={OperatorOptions.find(
                            (option) => option.value === condition.operator,
                        )}
                        onChange={(option) => onChange({ ...condition, operator: option.value })}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        gridColumn:
                            conditionIndex === 0
                                ? `4 / span ${readOnly ? 4 : 2}`
                                : `5${readOnly ? '/ span 2' : ''}`,
                    }}
                >
                    <div style={{ flexGrow: 1 }}>
                        <NumberInputFormik
                            id={`${conditionArrHelpers.name}.${conditionIndex}.value`}
                            required
                            disabled={readOnly}
                        />
                    </div>
                    {conditionIndex > 0 &&
                        parenComponent({
                            isStartParen: false,
                            question,
                            rule,
                            conditionArrHelpers,
                            conditionIndex,
                            readOnly,
                        })}
                </div>
            </>
        );
    } else if (
        question.component === ComponentTypes.MULTI_SELECT ||
        question.component === ComponentTypes.RADIO
    ) {
        const optionOptions = question.options.map((option) => ({
            value: option.option_id,
            name: option.answer,
        }));
        conditionComponent = (
            <>
                {conditionIndex === 0 && (
                    <div style={{ gridColumn: 2 }}>
                        <SuggestSingleSelectFormik
                            id={`${conditionArrHelpers.name}.${conditionIndex}.condition`}
                            required
                            disabled={readOnly}
                            values={NegationOptions}
                            selectedItem={NegationOptions.find(
                                (option) => option.value === condition.condition,
                            )}
                            onChange={(option) =>
                                onChange({ ...condition, condition: option.value })
                            }
                        />
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        gridColumn:
                            conditionIndex === 0
                                ? `3 / span ${readOnly ? 5 : 3}`
                                : `4 / span ${readOnly ? 4 : 2}`,
                    }}
                >
                    {conditionIndex > 0 ? (
                        <div className="sds_question-logic__rule-text padding__right--medium">
                            Is
                        </div>
                    ) : (
                        parenComponent({
                            isStartParen: true,
                            question,
                            rule,
                            conditionArrHelpers,
                            conditionIndex,
                            readOnly,
                        })
                    )}
                    <div style={{ flexDirection: 'row', flexGrow: 1 }}>
                        <SuggestSingleSelectFormik
                            id={`${conditionArrHelpers.name}.${conditionIndex}.value`}
                            required
                            disabled={readOnly}
                            values={optionOptions}
                            selectedItem={optionOptions.find(
                                (option) => option.value === condition.value,
                            )}
                            onChange={(option) => onChange({ ...condition, value: option.value })}
                        />
                    </div>
                    {conditionIndex > 0 &&
                        parenComponent({
                            isStartParen: false,
                            question,
                            rule,
                            conditionArrHelpers,
                            conditionIndex,
                            readOnly,
                        })}
                </div>
            </>
        );
    }
    return (
        <div className="sds_question-logic__rule-row">
            {conditionIndex > 0 && (
                <>
                    <div style={{ gridColumn: 2 }}>
                        <SuggestSingleSelectFormik
                            id={`${conditionArrHelpers.name}.${conditionIndex}.condition`}
                            required
                            disabled={readOnly}
                            values={conditionOptions}
                            selectedItem={conditionOptions.find(
                                (option) => option.value === condition.condition,
                            )}
                            onChange={(option) =>
                                onChange({ ...condition, condition: option.value })
                            }
                        />
                    </div>
                    <div style={{ display: 'flex' }}>
                        {parenComponent({
                            isStartParen: true,
                            question,
                            rule,
                            conditionArrHelpers,
                            conditionIndex,
                            readOnly,
                        })}
                        <div style={{ flexGrow: 1 }}>
                            <SuggestSingleSelectFormik
                                id={`${conditionArrHelpers.name}.${conditionIndex}.control`}
                                required
                                disabled={readOnly}
                                values={controlOptions}
                                selectedItem={controlOptions.find(
                                    (option) => option.value === condition.control,
                                )}
                                onChange={(option) =>
                                    onChange({
                                        condition: condition.condition,
                                        control: option.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </>
            )}
            {conditionComponent}
            {conditionIndex > 0 && !readOnly && (
                <Icon
                    className="margin__top--small"
                    intent={Icon.Intent.SECONDARY}
                    title="Remove"
                    icon={IconNames.CROSS}
                    iconSize={20}
                    onClick={() => conditionArrHelpers.remove(conditionIndex)}
                />
            )}
        </div>
    );
};

export const QuestionLogic = ({ question, questionIndex, questionSelectOptions, readOnly }) => {
    const controlOptions = useMemo(() => getControlOptions(question), [question]);
    const isAutoAccept = question.component === ComponentTypes.AUTO_ACCEPT;
    return (
        <FormInputContainer className="margin__bottom--small">
            <div className="sds_question-logic__title padding__bottom--medium">
                {formatQuestionLabel(`${question.question_id}. ${question.question_text}`)}
            </div>
            <FieldArray name={`questions.${questionIndex}.option_validation`}>
                {(ruleArrHelpers) =>
                    question.option_validation.map((rule, ruleIndex) => {
                        const isNoValidation = ruleIndex === 0 && rule.rule === '{}';
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div className="sds_question-logic__rule" key={ruleIndex.toString()}>
                                <FieldArray name={`${ruleArrHelpers.name}.${ruleIndex}.conditions`}>
                                    {(conditionArrHelpers) =>
                                        rule.conditions.map((condition, conditionIndex) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <Fragment key={conditionIndex.toString()}>
                                                {conditionIndex === 0 && (
                                                    <div className="sds_question-logic__rule-row">
                                                        <div className="sds_question-logic__rule-text">
                                                            If
                                                        </div>
                                                        <div
                                                            style={{
                                                                gridColumn: `2 / span ${
                                                                    readOnly ? 6 : 4
                                                                }`,
                                                            }}
                                                        >
                                                            <SuggestSingleSelectFormik
                                                                id={`${conditionArrHelpers.name}.${conditionIndex}.control`}
                                                                required
                                                                disabled={readOnly}
                                                                values={controlOptions}
                                                                selectedItem={controlOptions.find(
                                                                    (option) =>
                                                                        option.value ===
                                                                        condition.control,
                                                                )}
                                                                onChange={(option) =>
                                                                    ruleArrHelpers.replace(
                                                                        ruleIndex,
                                                                        {
                                                                            ...rule,
                                                                            conditions: [
                                                                                {
                                                                                    control:
                                                                                        option.value,
                                                                                },
                                                                            ],
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        {!readOnly && (
                                                            <div
                                                                style={{
                                                                    gridColumn: 6,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <ActionButtonText
                                                                    label="Add Condition"
                                                                    leftIcon={IconNames.ADD}
                                                                    onClick={() =>
                                                                        conditionArrHelpers.push({
                                                                            condition: 'OR',
                                                                            control:
                                                                                condition.control,
                                                                        })
                                                                    }
                                                                />
                                                                <Icon
                                                                    intent={Icon.Intent.SECONDARY}
                                                                    title="Remove"
                                                                    icon={IconNames.CROSS}
                                                                    iconSize={20}
                                                                    onClick={() =>
                                                                        question.option_validation
                                                                            .length === 1
                                                                            ? ruleArrHelpers.replace(
                                                                                  ruleIndex,
                                                                                  {
                                                                                      rule: '{}',
                                                                                      conditions:
                                                                                          [],
                                                                                  },
                                                                              )
                                                                            : ruleArrHelpers.remove(
                                                                                  ruleIndex,
                                                                              )
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {getComponentsForCondition({
                                                    condition,
                                                    question,
                                                    rule,
                                                    conditionArrHelpers,
                                                    conditionIndex,
                                                    controlOptions,
                                                    readOnly,
                                                })}
                                            </Fragment>
                                        ))
                                    }
                                </FieldArray>
                                <div
                                    className={`sds_question-logic__rule-row${
                                        isNoValidation ? '--no-validation' : ''
                                    }`}
                                >
                                    <div
                                        className={classNames('sds_question-logic__rule-text', {
                                            'padding__right--medium': isNoValidation,
                                        })}
                                    >
                                        {isNoValidation ? 'Always Jump To' : 'Then'}
                                    </div>
                                    <div
                                        style={{
                                            flexGrow: 1,
                                            gridColumn: `2 / span ${readOnly ? 6 : 4}`,
                                        }}
                                    >
                                        <SuggestSingleSelectFormik
                                            id={`${ruleArrHelpers.name}.${ruleIndex}.question_id`}
                                            required
                                            disabled={readOnly || isAutoAccept}
                                            values={questionSelectOptions}
                                            selectedItem={questionSelectOptions.find(
                                                (option) => option.value === rule.question_id,
                                            )}
                                            onChange={(newValue) =>
                                                ruleArrHelpers.replace(ruleIndex, {
                                                    ...rule,
                                                    question_id: newValue.value,
                                                })
                                            }
                                        />
                                    </div>
                                    {isNoValidation && !readOnly && controlOptions?.length > 0 && (
                                        <div className="padding__left--medium">
                                            <ActionButtonText
                                                label="Add Logic"
                                                leftIcon={IconNames.ADD}
                                                onClick={() =>
                                                    ruleArrHelpers.replace(ruleIndex, {
                                                        conditions: [{}],
                                                    })
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                {!isNoValidation &&
                                    ruleIndex === question.option_validation.length - 1 &&
                                    !readOnly && (
                                        <div className="padding__top--small">
                                            <ActionButtonText
                                                label="Add Rule"
                                                leftIcon={IconNames.ADD}
                                                onClick={() =>
                                                    ruleArrHelpers.push({ conditions: [{}] })
                                                }
                                            />
                                        </div>
                                    )}
                            </div>
                        );
                    })
                }
            </FieldArray>
        </FormInputContainer>
    );
};

QuestionLogic.propTypes = {
    question: PropTypes.shape({
        question_id: PropTypes.number.isRequired,
        question_text: PropTypes.string.isRequired,
        component: PropTypes.oneOf(Object.values(ComponentTypes)).isRequired,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                option_id: PropTypes.number.isRequired,
                answer: PropTypes.string.isRequired,
            }),
        ),
        option_validation: PropTypes.arrayOf(
            PropTypes.shape({
                rule: PropTypes.string,
                question_id: PropTypes.number,
            }),
        ).isRequired,
    }).isRequired,
    questionIndex: PropTypes.number.isRequired,
    questionSelectOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    readOnly: PropTypes.bool.isRequired,
};
